import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Button,
} from '@mui/material';
import { ActivityHistoryDocument } from '@/types/activityHistory';
import ActivityHistoryService from '@/services/activityHistoryService';

interface ActivityHistoryItemProps {
  history: ActivityHistoryDocument;
  isHost?: boolean;
  onRestore?: (history: ActivityHistoryDocument) => void;
}

const ActivityHistoryItem: React.FC<ActivityHistoryItemProps> = ({
  history,
  isHost = false,
  onRestore
}) => {
  const activityHistoryService = new ActivityHistoryService();
  
  const {
    actionType,
    description,
    userName,
    createdAt,
    metadata
  } = history;

  const isRestorable = metadata?.restorable && isHost;
  const isUpdate = metadata?.isUpdate;
  const hasDataComparison = actionType === 'EXPENSE_UPDATED' && metadata?.oldData;
  const hasDeletedData = metadata?.deletedData;

  // 데이터 입력 날짜 포맷팅 (YY/MM/DD 형식)
  const formatDataInputDate = (metadata: any) => {
    try {
      const deletedData = metadata?.deletedData;
      const oldData = metadata?.oldData;
      const dataInfo = oldData || deletedData;
      
      // createdTime 필드 확인 (Firebase Timestamp 또는 Date)
      let dateToFormat: Date | null = null;
      
      if (dataInfo?.createdTime) {
        if (dataInfo.createdTime.toDate) {
          // Firebase Timestamp
          dateToFormat = dataInfo.createdTime.toDate();
        } else if (dataInfo.createdTime instanceof Date) {
          // Date 객체
          dateToFormat = dataInfo.createdTime;
        } else {
          // 문자열이나 숫자
          dateToFormat = new Date(dataInfo.createdTime);
        }
      } else if (dataInfo?.createTime) {
        // 기존 createTime 필드도 확인
        dateToFormat = new Date(dataInfo.createTime);
      }
      
      if (dateToFormat && !isNaN(dateToFormat.getTime())) {
        const year = dateToFormat.getFullYear().toString().substring(2);
        const month = (dateToFormat.getMonth() + 1).toString().padStart(2, '0');
        const day = dateToFormat.getDate().toString().padStart(2, '0');
        return `${year}/${month}/${day}`;
      }
    } catch (e) {
      console.error('데이터 입력 날짜 추출 오류:', e);
    }
    return '알 수 없음';
  };

  // 활동 날짜 포맷팅
  const formatActivityDate = (date: Date | any) => {
    try {
      const activityDate = date instanceof Date ? date : new Date(date);
      const now = new Date();
      const diffMs = now.getTime() - activityDate.getTime();
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      if (diffMinutes < 1) return '방금 전';
      if (diffMinutes < 60) return `${diffMinutes}분 전`;
      if (diffHours < 24) return `${diffHours}시간 전`;
      if (diffDays < 7) return `${diffDays}일 전`;
      
      const year = activityDate.getFullYear().toString().substring(2);
      const month = (activityDate.getMonth() + 1).toString().padStart(2, '0');
      const day = activityDate.getDate().toString().padStart(2, '0');
      return `${year}/${month}/${day}`;
    } catch (e) {
      return '알 수 없음';
    }
  };

  const handleRestore = () => {
    if (onRestore) {
      onRestore(history);
    }
  };

  return (
    <Card 
      sx={{ 
        mb: 2, 
        background: 'var(--bg-base)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 2,
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: 'var(--shadow-1)',
        }
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* 메인 콘텐츠 */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          {/* 아이콘 */}
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: 'var(--bg-elev-1)',
              border: '1px solid var(--border-subtle)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.2rem',
              color: 'var(--fg-secondary)',
              flexShrink: 0
            }}
          >
            {activityHistoryService.getActivityIcon(actionType, metadata)}
          </Box>

          {/* 콘텐츠 */}
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            {/* 설명 */}
            <Typography 
              variant="body1" 
              sx={{ 
                fontWeight: 500, 
                color: 'var(--fg-strong)',
                mb: 1,
                lineHeight: 1.4
              }}
            >
              {activityHistoryService.generateDynamicDescription(actionType, metadata, description)}
            </Typography>

            {/* 간단한 메타 정보 */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Typography variant="caption" sx={{ color: 'var(--fg-secondary)' }}>
                {userName} • {formatActivityDate(createdAt)}
              </Typography>
              {/* 데이터 입력일 표시 (삭제/수정된 데이터가 있는 경우만) */}
              {(hasDeletedData || hasDataComparison) && (
                <Typography variant="caption" sx={{ color: 'var(--fg-secondary)', fontWeight: 500 }}>
                  입력일: {formatDataInputDate(metadata)}
                </Typography>
              )}
            </Box>

          </Box>

          {/* 액션 버튼들 */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isRestorable && (
              <Button variant="text" size="small" onClick={handleRestore}>
                {isUpdate ? '되돌리기' : '복원'}
              </Button>
            )}
          </Box>
        </Box>

      </CardContent>
    </Card>
  );
};


export default ActivityHistoryItem;
